import { useState, useEffect } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { Home, Users, Receipt, CreditCard, Bell, BarChart3, LogOut, UserCog, Upload, Settings, Building2, Dumbbell, Briefcase, Store, MessageSquare, Scissors, Stethoscope, CalendarDays, Menu, X, Wallet, Megaphone, Network, FileText, Send, LifeBuoy } from 'lucide-react';
import { Button } from '@/components/ui/button';

const APPOINTMENT_TYPES = ['barber', 'beauty', 'dental', 'gym'];

const businessTypeDisplay = {
  apartment: { name: 'Apartman/Site', icon: Building2, color: 'bg-blue-500' },
  gym: { name: 'Spor Salonu', icon: Dumbbell, color: 'bg-orange-500' },
  accounting: { name: 'Muhasebe', icon: Briefcase, color: 'bg-purple-500' },
  general: { name: 'Genel İşletme', icon: Store, color: 'bg-green-500' },
  barber: { name: 'Erkek Kuaför / Berber', icon: Scissors, color: 'bg-blue-600' },
  beauty: { name: 'Kadın Kuaför / Güzellik', icon: Scissors, color: 'bg-pink-500' },
  dental: { name: 'Diş Kliniği', icon: Stethoscope, color: 'bg-cyan-600' },
};

export const Sidebar = () => {
  const { user, logout } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const [mobileOpen, setMobileOpen] = useState(false);

  useEffect(() => { setMobileOpen(false); }, [location.pathname]);

  const handleLogout = async () => { await logout(); navigate('/login'); };

  const isAdmin = user?.role === 'admin';
  const isStaff = user?.role === 'staff';
  const isAdminOrStaff = isAdmin || isStaff;
  const labels = user?.labels || { people: 'Kullanıcılar', payment: 'Ödemeler', identifier: 'Numara' };
  const btype = user?.business_type || 'general';
  const isAppointmentType = APPOINTMENT_TYPES.includes(btype);
  const typeConfig = businessTypeDisplay[btype] || businessTypeDisplay.general;
  const pkg = user?.package || {};
  const allowBranches = user?.allow_branches === true;

  /**
   * Permission gate for staff users. Admin (owner) and superadmin always
   * return true. Staff must have `permissions[key]` === true. Missing keys
   * default to false (deny-by-default — mirrors backend `_user_has_permission`).
   */
  const can = (key) => {
    if (!isStaff) return true; // owner/superadmin/customer bypass
    const perms = user?.permissions || {};
    return perms[key] === true;
  };

  const buildAdminPaymentLinks = () => {
    const links = [
      { to: '/admin/dashboard', icon: Home, label: 'Ana Sayfa' },
    ];
    if (can('customers')) links.push({ to: '/admin/users', icon: Users, label: labels.people });
    if (!isStaff && pkg.bulk_import !== false) links.push({ to: '/admin/bulk-import', icon: Upload, label: 'Toplu Aktarım' });
    if (pkg.payments !== false && can('payments')) {
      links.push({ to: '/admin/payments', icon: Receipt, label: labels.payment });
      links.push({ to: '/admin/payment-records', icon: CreditCard, label: `${labels.payment} Kayıtları` });
    }
    if (btype === 'apartment' && pkg.expenses !== false && can('payments')) links.push({ to: '/admin/expenses', icon: Wallet, label: 'Giderler' });
    if (btype === 'apartment' && pkg.announcements !== false && can('messaging')) links.push({ to: '/admin/announcements', icon: Megaphone, label: 'Duyurular' });
    if (!isStaff && pkg.reports !== false) links.push({ to: '/admin/reports', icon: BarChart3, label: 'Raporlar' });
    if (pkg.messaging !== false && can('messaging')) links.push({ to: '/admin/messages', icon: MessageSquare, label: 'Mesajlar' });
    if (!isStaff) links.push({ to: '/admin/sms-logs', icon: Send, label: 'SMS Sonuçları' });
    if (!isStaff && allowBranches) links.push({ to: '/admin/branches', icon: Network, label: 'Şubeler' });
    if (!isStaff) links.push({ to: '/admin/billing', icon: FileText, label: 'Fatura Bilgileri' });
    if (!isStaff) links.push({ to: '/admin/settings', icon: Settings, label: 'Ayarlar' });
    links.push({ to: '/profile', icon: UserCog, label: 'Profil' });
    return links;
  };

  const buildAdminAppointmentLinks = () => {
    const links = [
      { to: '/admin/dashboard', icon: Home, label: 'Ana Sayfa' },
    ];
    // Randevular surfaced right after "Ana Sayfa" — this is the primary
    // module for appointment-style sectors (Berber, Kuaför, Klinik, Psikolog,
    // Spor Salonu). See APPOINTMENT_TYPES for the full list.
    if (pkg.appointments !== false && can('appointments')) links.push({ to: '/admin/appointments', icon: CalendarDays, label: 'Randevular' });
    if (can('customers')) links.push({ to: '/admin/users', icon: Users, label: labels.people });
    if (!isStaff && pkg.bulk_import !== false) links.push({ to: '/admin/bulk-import', icon: Upload, label: 'Toplu Aktarım' });
    if (pkg.messaging !== false && can('messaging')) links.push({ to: '/admin/messages', icon: MessageSquare, label: 'Mesajlar' });
    if (!isStaff) links.push({ to: '/admin/sms-logs', icon: Send, label: 'SMS Sonuçları' });
    if (!isStaff && allowBranches) links.push({ to: '/admin/branches', icon: Network, label: 'Şubeler' });
    if (!isStaff) links.push({ to: '/admin/billing', icon: FileText, label: 'Fatura Bilgileri' });
    if (!isStaff) links.push({ to: '/admin/settings', icon: Settings, label: 'Ayarlar' });
    links.push({ to: '/profile', icon: UserCog, label: 'Profil' });
    return links;
  };

  const buildUserPaymentLinks = () => {
    const links = [{ to: '/dashboard', icon: Home, label: 'Ana Sayfa' }];
    if (pkg.payments !== false) links.push({ to: '/payments', icon: Receipt, label: `${labels.payment}im` });
    if (btype === 'apartment' && pkg.expenses !== false) links.push({ to: '/expenses', icon: Wallet, label: 'Giderler' });
    if (btype === 'apartment' && pkg.announcements !== false) links.push({ to: '/announcements', icon: Megaphone, label: 'Duyurular' });
    links.push({ to: '/notifications', icon: Bell, label: 'Bildirimler' });
    links.push({ to: '/profile', icon: UserCog, label: 'Profil' });
    return links;
  };

  const buildUserAppointmentLinks = () => {
    const links = [{ to: '/dashboard', icon: Home, label: 'Ana Sayfa' }];
    if (pkg.appointments !== false) links.push({ to: '/appointments', icon: CalendarDays, label: 'Randevularım' });
    links.push({ to: '/notifications', icon: Bell, label: 'Bildirimler' });
    links.push({ to: '/profile', icon: UserCog, label: 'Profil' });
    return links;
  };

  let links;
  if (isAdminOrStaff) {
    links = isAppointmentType ? buildAdminAppointmentLinks() : buildAdminPaymentLinks();
  } else {
    links = isAppointmentType ? buildUserAppointmentLinks() : buildUserPaymentLinks();
  }
  // "Destek" appears for every authenticated role (owner/staff/customer) —
  // placed right before Profil so it's reachable from anywhere in the app.
  const profileIdx = links.findIndex((l) => l.to === '/profile');
  const supportLink = { to: '/support', icon: LifeBuoy, label: 'Destek' };
  if (profileIdx >= 0) links.splice(profileIdx, 0, supportLink);
  else links.push(supportLink);

  const bottomLinks = links.slice(0, 5);

  return (
    <>
      {/* Mobile top bar - Logo + name on left, business name on right (truncate) */}
      <div className="lg:hidden fixed top-0 left-0 right-0 h-16 bg-white border-b border-[#E5E5E5] z-40 flex items-center px-3 gap-2" data-testid="mobile-topbar">
        <button onClick={() => setMobileOpen(true)} className="p-1.5 -ml-1 shrink-0" data-testid="open-sidebar">
          <Menu className="w-6 h-6 text-[#1A1A1A]" />
        </button>
        <img src="/logo-paydaai.png" alt="Paydaai" className="h-10 w-auto max-w-[150px] object-contain shrink-0 drop-shadow-sm" data-testid="mobile-topbar-logo" />
        <div className="flex-1 min-w-0 flex justify-end">
          <span className="text-xs font-medium text-[#1A1A2E] truncate max-w-[50%]" title={user?.business_name}>
            {user?.business_name || typeConfig.name}
          </span>
        </div>
      </div>

      {/* Mobile overlay */}
      {mobileOpen && (
        <div className="lg:hidden fixed inset-0 bg-black/40 z-50" onClick={() => setMobileOpen(false)} data-testid="sidebar-overlay" />
      )}

      {/* Sidebar */}
      <aside
        className={`fixed lg:sticky top-0 left-0 h-screen bg-white border-r border-[#E5E5E5] flex flex-col z-50 transition-transform duration-300 w-64 ${
          mobileOpen ? 'translate-x-0' : '-translate-x-full lg:translate-x-0'
        }`}
        data-testid="sidebar"
      >
        {/* Sidebar header - Logo prominent, business name below */}
        <div className="p-4 border-b border-[#E5E5E5]">
          <div className="flex flex-col items-center gap-1.5 relative">
            <button onClick={() => setMobileOpen(false)} className="lg:hidden absolute top-0 right-0 p-1" data-testid="close-sidebar">
              <X className="w-5 h-5 text-[#595959]" />
            </button>
            <img src="/logo-paydaai.png" alt="Paydaai" className="h-12 w-auto object-contain drop-shadow-md" />
          </div>
          <div className="mt-3 px-1 text-center">
            <p className="text-sm font-medium text-[#1A1A2E] truncate" data-testid="sidebar-business-name">
              {user?.business_name || typeConfig.name}
            </p>
            <p className="text-[11px] text-[#999]">{typeConfig.name}</p>
          </div>
        </div>

        {/* Nav links */}
        <nav className="flex-1 p-3 space-y-0.5 overflow-y-auto">
          {links.map((link) => {
            const Icon = link.icon;
            const isActive = location.pathname === link.to;
            return (
              <Link
                key={link.to}
                to={link.to}
                onClick={() => setMobileOpen(false)}
                className={`flex items-center gap-3 px-3 py-2.5 rounded-xl text-sm transition-all ${
                  isActive
                    ? 'bg-[#2563EB] text-white font-medium shadow-sm'
                    : 'text-[#475569] hover:bg-[#EFF6FF] hover:text-[#1E4ED8]'
                }`}
                data-testid={`nav-${link.to.replace(/\//g, '-').slice(1)}`}
              >
                <Icon className="w-[18px] h-[18px]" strokeWidth={isActive ? 2 : 1.5} />
                <span>{link.label}</span>
              </Link>
            );
          })}
        </nav>

        {/* User info + logout */}
        <div className="p-3 border-t border-[#E5E5E5]">
          <div className="mb-3 px-3">
            <p className="text-sm font-medium text-[#1A1A2E] truncate" data-testid="user-name">{user?.name}</p>
            <p className="text-[11px] text-[#999] truncate" data-testid="user-email">{user?.email}</p>
          </div>
          <Button onClick={handleLogout} variant="ghost" className="w-full justify-start gap-3 text-[#595959] hover:text-red-600 hover:bg-red-50 h-9" data-testid="logout-button">
            <LogOut className="w-4 h-4" strokeWidth={1.5} /> Çıkış Yap
          </Button>
        </div>
      </aside>

      {/* Mobile bottom nav - cleaner design */}
      <nav className="lg:hidden fixed bottom-0 left-0 right-0 bg-white border-t border-[#E5E5E5] z-40 pb-[env(safe-area-inset-bottom)]" data-testid="bottom-nav">
        <div className="flex items-stretch h-14">
          {bottomLinks.map((link) => {
            const Icon = link.icon;
            const isActive = location.pathname === link.to;
            return (
              <Link
                key={link.to}
                to={link.to}
                className={`flex-1 flex flex-col items-center justify-center gap-0.5 transition-colors ${
                  isActive ? 'text-[#2563EB]' : 'text-[#999]'
                }`}
                data-testid={`bottom-${link.label.toLowerCase().replace(/\s+/g, '-')}`}
              >
                <Icon className="w-5 h-5" strokeWidth={isActive ? 2.5 : 1.5} />
                <span className={`text-[10px] leading-tight truncate max-w-[52px] ${isActive ? 'font-semibold' : ''}`}>{link.label}</span>
              </Link>
            );
          })}
        </div>
      </nav>
    </>
  );
};
